import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import TypographyOverride from "../components/TypographyOverride";
import TocRail from "../components/TocRail";
import { MK_DEFAULTS } from "../lib/marketingDefaults";
import {
  InsightDiagram,
  ValueDiagram,
  ContentDiagram,
  DistributionDiagram,
  FunnelDiagram,
  SystemDiagram,
} from "../components/MarketingDiagrams";

const G = ({ children }) => <span className="mk-gold">{children}</span>;

const Brand = ({ slug, children }) => (
  <Link to={`/blog/${slug}`} className="mk-brand-link" data-testid={`brand-${slug}`}>
    {children}
  </Link>
);

/* Section shell. Generous spacing — every section breathes. */
function Section({ number, title, anchorId, children, panel = false, testid }) {
  return (
    <section
      data-testid={testid}
      id={anchorId}
      className={`relative ${panel ? "mk-panel" : ""} px-6 md:px-12 lg:px-20 py-24 md:py-40`}
    >
      <div className="max-w-5xl mx-auto">
        {(number || title) && (
          <header className="mb-14 md:mb-24">
            {number && (
              <p className="mk-section-label text-[#C9A050]/70 mb-7">{number}</p>
            )}
            {title && (
              <h2 className="mk-section-title text-white max-w-4xl">{title}</h2>
            )}
          </header>
        )}
        <div className="space-y-8 md:space-y-12 mk-body text-[#D4D4D8]">
          {children}
        </div>
      </div>
    </section>
  );
}

function Final({ children, fieldId }) {
  return (
    <p
      data-testid="mk-final-statement"
      data-field={fieldId}
      className="mk-final text-[#E4E4E7] my-16 md:my-24 max-w-3xl"
    >
      {children}
    </p>
  );
}

function Divider() {
  return (
    <div className="px-6 md:px-12 lg:px-20">
      <div className="max-w-5xl mx-auto mk-divider" />
    </div>
  );
}

/* Renders text from `data` with `MK_DEFAULTS` fallback, plus a data-field attr
   for admin tooling and per-field typography zone overrides via CSS. */
function makeText(data) {
  return (id, fallback) => {
    const v = data?.[id];
    return v !== undefined && v !== null && v !== "" ? v : fallback ?? MK_DEFAULTS[id] ?? "";
  };
}

export default function Marketing() {
  const [data, setData] = useState(null);

  useEffect(() => {
    document.title = "How We Build Marketing — Artistry Perspectives";
    window.scrollTo(0, 0);
    api.get("/marketing").then(({ data }) => setData(data || {}));
  }, []);

  const T = makeText(data || {});
  const examples = data?.brand_examples || [];
  const brandLogo = data?.brand_logo_url;
  const brandLogoVisible = data?.brand_logo_visible !== false;
  const brandLogoMono = !!data?.brand_logo_monochrome;

  // Highlight rules — admin-controlled gold + underlined words.
  const goldList = useMemo(
    () => parseHighlightList(data?.gold_words),
    [data?.gold_words],
  );
  const underlineList = useMemo(
    () => parseHighlightList(data?.underline_words),
    [data?.underline_words],
  );
  useHighlights(goldList, underlineList, [data]);

  const scrollToAnchor = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main data-testid="marketing-page" className="mk-page bg-[#0A0A0C] overflow-x-hidden">
      <TypographyOverride pageId="marketing" scopeSelector=".mk-page" prefix="mk" />
      <TocRail
        sections={[
          { id: "mk-anchor-audience", label: "Target Audience" },
          { id: "mk-anchor-insight", label: "Insight" },
          { id: "mk-anchor-value", label: "Value" },
          { id: "mk-anchor-positioning", label: "Positioning" },
          { id: "mk-anchor-brand-strategy", label: "Brand Strategy" },
          { id: "mk-anchor-content", label: "Content" },
          { id: "mk-anchor-distribution", label: "Distribution" },
          { id: "mk-anchor-funnel", label: "Funnel" },
        ]}
      />

      {/* HERO */}
      <section data-testid="mk-hero" className="px-6 md:px-12 lg:px-20 pt-40 md:pt-52 pb-28 md:pb-40 text-center">
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mk-section-label text-[#C9A050]/70 mb-10">
          {T("hero_overline")}
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }} className="mk-hero-title text-white max-w-5xl mx-auto">
          {T("hero_title_lead")}
          <span className="font-serif-accent text-[#C9A050]">{T("hero_title_accent")}</span>
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.25 }} className="mk-hero-body text-[#D4D4D8] max-w-2xl mx-auto mt-12 md:mt-16">
          {T("hero_question")}
        </motion.p>
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.4 }} className="mk-hero-body text-[#A1A1AA] max-w-2xl mx-auto mt-5">
          {T("hero_definition")}
        </motion.p>
      </section>

      {/* PILLARS — same premium layout as the Home "How we think" band */}
      <section data-testid="mk-pillars" className="mk-panel px-6 md:px-12 lg:px-20 py-24 md:py-36">
        <div className="max-w-5xl mx-auto">
          <p className="mk-section-label text-[#C9A050]/70 mb-16 md:mb-20">{T("pillars_overline")}</p>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-y-16 md:gap-y-0 md:gap-x-6 items-stretch">
            {[1, 2, 3].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.7, delay: 0.05 + (i - 1) * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="col-span-12 md:col-span-4 relative"
              >
                <div className="flex flex-col h-full">
                  <div className="flex items-center gap-4 mb-7">
                    <span className="font-serif-accent text-[#C9A050] text-3xl md:text-4xl leading-none">0{i}</span>
                    <span className="h-px flex-1 bg-gradient-to-r from-[#C9A050]/35 to-transparent" />
                  </div>
                  <h3 className="mk-pillar-title text-white mb-4">{T(`pillar_${i}_title`)}</h3>
                  <p className="mk-pillar-body text-[#A1A1AA] max-w-md">{T(`pillar_${i}_body`)}</p>
                </div>
                {i < 3 && (
                  <span
                    aria-hidden="true"
                    className="hidden md:block absolute top-[18px] -right-3 w-6 border-t border-dotted border-[#C9A050]/40"
                  />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      {/* INTRO */}
      <Section testid="mk-intro" title={<>{T("intro_title")}<br /><span className="text-[#A1A1AA] mk-section-title" style={{ fontSize: "0.55em" }}>{T("intro_subtitle")}</span></>}>
        <p>{T("intro_line_1")}</p>
        <p>Example: <G>Sim's Glow Cream</G> (Skincare Brand)</p>

        <div className="mt-14 md:mt-20 mk-card-gold p-7 md:p-12">
          <p className="overline text-[#C9A050]/70 mb-8">{T("bb_overline")}</p>
          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-y-6 gap-x-8 text-[#D4D4D8] items-start">
            <span className="text-[#8A8A93] text-sm">{T("bb_brand_name_label")}</span>
            <span className="flex items-center gap-4 flex-wrap">
              {brandLogoVisible && brandLogo && (
                <img src={brandLogo} alt="" className={`h-9 md:h-10 w-auto object-contain ${brandLogoMono ? "mk-logo-mono" : ""}`} />
              )}
              <G>{T("bb_brand_name_value")}</G>
            </span>
            <span className="text-[#8A8A93] text-sm">{T("bb_logo_label")}</span>
            <span>
              {brandLogoVisible && brandLogo ? (
                <img src={brandLogo} alt="" className={`h-12 md:h-14 w-auto object-contain ${brandLogoMono ? "mk-logo-mono" : ""}`} />
              ) : (
                <span className="text-[#8A8A93] italic">(visual)</span>
              )}
            </span>
            <span className="text-[#8A8A93] text-sm">{T("bb_tagline_label")}</span>
            <span>{T("bb_tagline_value")}</span>
            <span className="text-[#8A8A93] text-sm">{T("bb_mission_label")}</span>
            <span>{T("bb_mission_value")}</span>
            <span className="text-[#8A8A93] text-sm">{T("bb_vision_label")}</span>
            <span>{T("bb_vision_value")}</span>
          </div>
        </div>

        <p className="mt-16 md:mt-20">{T("intro_question_lead")}</p>
        <p className="mk-quote text-[#E4E4E7]">{T("intro_question_quote")}</p>
        <p className="text-[#8A8A93] text-sm md:text-base italic mt-8">{T("intro_note")}</p>
      </Section>

      <Divider />

      {/* 01 TARGET AUDIENCE */}
      <Section testid="mk-section-1" anchorId="mk-anchor-audience" number={T("s1_overline")} title={T("s1_title")}>
        <p className="font-display text-3xl md:text-4xl text-white">{T("s1_emphasis")}</p>
        <p>{T("s1_body_1")}</p>
        <p>{T("s1_body_2")}</p>

        <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
          <div>
            <p className="overline text-[#8A8A93]/80 mb-6"><G>{T("s1_for_label")}</G></p>
            <ul className="space-y-4 text-[#D4D4D8]">
              {[T("s1_neg_1"), T("s1_neg_2")].map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="text-[#8A8A93] mt-1.5">✕</span>
                  <span className="text-[#8A8A93] line-through decoration-[#8A8A93]/40">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mk-card-gold p-7 md:p-9">
            <p className="overline text-[#C9A050]/80 mb-6">{T("s1_right_label")}</p>
            <p className="font-display text-3xl md:text-4xl text-white leading-tight">{T("s1_right_headline")}</p>
            <p className="text-[#C9A050] mt-2 font-medium tracking-wide">{T("s1_right_age")}</p>
            <ul className="mt-7 space-y-3 text-[#E4E4E7]">
              <li>{T("s1_right_trait_1")}</li>
              <li>{T("s1_right_trait_2")}</li>
            </ul>
            {T("s1_specificity_note") && (
              <p data-testid="mk-s1-specificity-note" data-field="s1_specificity_note" className="font-serif-accent italic text-[#C9A050] text-base md:text-lg leading-snug mt-7 pt-6 border-t border-[#C9A050]/15">
                {T("s1_specificity_note")}
              </p>
            )}
          </div>
        </div>

        <Final fieldId="s1_final">{T("s1_final")}</Final>
      </Section>

      <Divider />

      {/* 02 INSIGHT */}
      <Section testid="mk-section-2" anchorId="mk-anchor-insight" number={T("s2_overline")} title={T("s2_title")} panel>
        <p>{T("s2_body_1")}</p>
        <p>{T("s2_body_2")}</p>
        <p className="text-[#8A8A93] text-sm md:text-base italic">{T("s2_body_3")}</p>

        <p className="mk-quote text-[#E4E4E7] mt-12 whitespace-pre-line">{T("s2_quote")}</p>

        <p className="mt-14">{T("s2_intro_to_diagram")}</p>

        <InsightDiagram />

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-12">
          <div className="mk-card-gold p-7 md:p-9">
            <p className="overline text-[#C9A050]/80 mb-5">{T("s2_pain_label")}</p>
            <p className="text-[#E4E4E7] leading-relaxed">{T("s2_pain_q1")}</p>
            <p className="text-[#E4E4E7] leading-relaxed mt-4">{T("s2_pain_q2")}</p>
          </div>
          <div className="mk-card-gold p-7 md:p-9">
            <p className="overline text-[#C9A050]/80 mb-5">{T("s2_desire_label")}</p>
            <p className="text-[#E4E4E7] leading-relaxed">{T("s2_desire_q1")}</p>
            <p className="text-[#E4E4E7] leading-relaxed mt-4">{T("s2_desire_q2")}</p>
          </div>
        </div>

        <Final fieldId="s2_final">{T("s2_final")}</Final>
      </Section>

      <Divider />

      {/* 03 VALUE */}
      <Section testid="mk-section-3" anchorId="mk-anchor-value" number={T("s3_overline")} title={T("s3_title")}>
        <p>{T("s3_body_1")}</p>
        <p>{T("s3_body_2")}</p>
        <p>{T("s3_body_3")}</p>

        <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
          <div className="mk-card-gold p-7 md:p-9">
            <p className="overline text-[#C9A050]/80 mb-6">{T("s3_feel_label")}</p>
            <ul className="space-y-4 text-[#E4E4E7]"><li>{T("s3_feel_1")}</li><li>{T("s3_feel_2")}</li></ul>
          </div>
          <div className="mk-card-gold p-7 md:p-9">
            <p className="overline text-[#C9A050]/80 mb-6">{T("s3_offer_label")}</p>
            <ul className="space-y-4 text-[#E4E4E7]">
              <li>{T("s3_offer_1")}</li><li>{T("s3_offer_2")}</li><li>{T("s3_offer_3")}</li><li>{T("s3_offer_4")}</li>
            </ul>
          </div>
        </div>

        <p className="mt-16 overline text-[#8A8A93]">{T("s3_connect_label")}</p>
        <ul className="space-y-3 text-[#D4D4D8]">
          <li>{T("s3_connect_1")}</li>
          <li>{T("s3_connect_2")}</li>
        </ul>

        <ValueDiagram />

        <p>{T("s3_close_line")}</p>

        <Final fieldId="s3_final">{T("s3_final")}</Final>
      </Section>

      <Divider />

      {/* 04 POSITIONING */}
      <Section testid="mk-section-4" anchorId="mk-anchor-positioning" number={T("s4_overline")} title={T("s4_title")} panel>
        <p>{T("s4_body_1")}</p>
        <p>{T("s4_body_2")}</p>
        <p>{T("s4_body_3")}</p>

        {/* Real brand examples — generous space above & below */}
        <div className="!mt-28 md:!mt-40 !mb-28 md:!mb-40">
          <p className="overline text-[#C9A050]/70 mb-14 md:mb-20">{T("s4_examples_label")}</p>
          <div className="space-y-12 md:space-y-16">
            {(examples.length > 0 ? examples : []).map((b) => (
              <BrandRow key={b.slug} {...b} />
            ))}
          </div>
        </div>

        {/* For Sim's Glow Cream — center-aligned, generous space */}
        <div className="!mt-28 md:!mt-40">
          <p className="overline text-[#8A8A93] mb-10 text-center"><G>{T("s4_for_label")}</G></p>
          <p className="text-[#D4D4D8] text-center">{T("s4_for_intro")}</p>
          <ul className="mt-12 md:mt-14 space-y-5 text-center">
            <li className="text-[#E4E4E7] text-lg md:text-xl">{T("s4_opt_1")}</li>
            <li className="text-[#8A8A93] text-sm tracking-widest uppercase">{T("s4_opt_or")}</li>
            <li className="text-[#E4E4E7] text-lg md:text-xl">{T("s4_opt_2")}</li>
            <li className="text-[#8A8A93] text-sm tracking-widest uppercase">{T("s4_opt_or")}</li>
            <li className="text-[#E4E4E7] text-lg md:text-xl">{T("s4_opt_3")}</li>
            <li className="text-[#8A8A93] text-sm tracking-widest uppercase">{T("s4_opt_or")}</li>
            <li className="text-[#E4E4E7] text-lg md:text-xl">{T("s4_opt_4")}</li>
          </ul>
          <p className="mt-16 md:mt-20 text-[#A1A1AA] text-center">{T("s4_after_1")}</p>
          <p className="mt-6 text-center">{T("s4_after_2")}</p>
        </div>

        {/* Generous spacing for everything below "each one feels different…" */}
        <p className="!mt-32 md:!mt-44 mk-quote text-[#E4E4E7]">{T("s4_quote")}</p>
        <p className="!mt-12 md:!mt-16">{T("s4_close_1")}</p>
        <p className="!mt-10 md:!mt-14">{T("s4_close_2")}</p>

        <Final fieldId="s4_final">{T("s4_final")}</Final>
      </Section>

      <Divider />

      {/* 05 BRAND STRATEGY */}
      <Section testid="mk-section-5" anchorId="mk-anchor-brand-strategy" number={T("s5_overline")} title={T("s5_title")}>
        <p>{T("s5_body_1")}</p>
        <p className="mk-quote text-[#E4E4E7]">{T("s5_quote_short")}</p>
        <p>{T("s5_body_2")}</p>
        <p>{T("s5_body_3")}</p>
        <p className="whitespace-pre-line">{T("s5_body_4")}</p>

        <div className="mt-16 md:mt-24 mk-card-gold p-7 md:p-12">
          <p className="overline text-[#C9A050]/80 mb-2"><G>{T("s5_card_for")}</G></p>
          <p className="text-[#E4E4E7] mb-10">{T("s5_card_positioning")}</p>
          <p className="overline text-[#8A8A93] mb-7">{T("s5_card_label")}</p>
          <dl className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-y-6 gap-x-6">
            <dt className="text-[#8A8A93] text-sm">{T("s5_tone_k")}</dt><dd className="text-[#E4E4E7]">{T("s5_tone_v")}</dd>
            <dt className="text-[#8A8A93] text-sm">{T("s5_personality_k")}</dt><dd className="text-[#E4E4E7]">{T("s5_personality_v")}</dd>
            <dt className="text-[#8A8A93] text-sm">{T("s5_messaging_k")}</dt><dd className="text-[#E4E4E7]">{T("s5_messaging_v")}</dd>
            <dt className="text-[#8A8A93] text-sm">{T("s5_style_k")}</dt><dd className="text-[#E4E4E7]">{T("s5_style_v")}</dd>
          </dl>
        </div>

        {/* Generous spacing for all lines below the card */}
        <p className="!mt-20 md:!mt-28">{T("s5_close_1")}</p>
        <p className="!mt-10 md:!mt-14">{T("s5_close_2")}</p>
        <p className="!mt-10 md:!mt-14">{T("s5_close_3")}</p>

        <p className="!mt-14 md:!mt-20 text-[#E4E4E7]">
          <span className="text-[#C9A050] mr-2">→</span>{T("s5_arrow")}
        </p>

        <Final fieldId="s5_final">{T("s5_final")}</Final>
      </Section>

      <Divider />

      {/* 06 CONTENT */}
      <Section testid="mk-section-6" anchorId="mk-anchor-content" number={T("s6_overline")} title={T("s6_title")} panel>
        <p>{T("s6_body_1")}</p>
        <p>{T("s6_body_2")}</p>
        <p>{T("s6_body_3")}</p>
        <p>{T("s6_body_4")}</p>

        <div className="!mt-16 md:!mt-24 grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8">
          <MiniBox label="Audience" body={T("s6_minibox_audience")} />
          <MiniBox label="Insight" body={T("s6_minibox_insight")} />
          <MiniBox label="Value" body={T("s6_minibox_value")} />
        </div>

        <ContentDiagram />

        {/* A/B/C — distinct categorised cards with generous spacing */}
        <div className="!mt-20 md:!mt-28 space-y-12 md:space-y-16">
          <div className="border-l-2 border-[#C9A050]/40 pl-7 md:pl-10">
            <h4 className="mk-card-title text-white mb-6">{T("s6_a_title")}</h4>
            <div className="space-y-7 text-[#D4D4D8]">
              <div>
                <p><span className="text-[#C9A050]">(<G>Insight</G>) Pain</span> → {T("s6_a_pain_q")}</p>
                <p className="text-[#A1A1AA] mt-2">{T("s6_a_pain_show")}</p>
                <p className="text-[#A1A1AA]">{T("s6_a_pain_outcome")}</p>
              </div>
              <div>
                <p><span className="text-[#C9A050]">(<G>Insight</G>) Desire</span> → {T("s6_a_desire_q")}</p>
                <p className="text-[#A1A1AA] mt-2">{T("s6_a_desire_show")}</p>
                <p className="text-[#A1A1AA]">{T("s6_a_desire_outcome")}</p>
              </div>
            </div>
          </div>

          <div className="border-l-2 border-[#C9A050]/40 pl-7 md:pl-10">
            <h4 className="mk-card-title text-white mb-6">{T("s6_b_title")}</h4>
            <p className="text-[#A1A1AA]">{T("s6_b_line_1")}</p>
            <p className="text-[#A1A1AA] mt-2">{T("s6_b_line_2")}</p>
          </div>

          <div className="border-l-2 border-[#C9A050]/40 pl-7 md:pl-10">
            <h4 className="mk-card-title text-white mb-6">{T("s6_c_title")}</h4>
            <p className="text-[#A1A1AA]">{T("s6_c_line_1")}</p>
            <p className="text-[#A1A1AA] mt-2">{T("s6_c_line_2")}</p>
          </div>
        </div>

        {/* Types of content — wrapped in a box */}
        <div className="!mt-24 md:!mt-32 mk-card-gold p-7 md:p-12">
          <h4 className="mk-card-title text-white mb-8">{T("s6_types_title")}</h4>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5 text-[#D4D4D8]">
            {[
              ["Authority", "Builds trust through knowledge"],
              ["Relatable", "Shows you understand them"],
              ["Problem–Solution", "Connects pain to value"],
              ["Proof", "Shows real results"],
              ["Educational", "Explains simply"],
              ["Storytelling", "Creates emotional connection"],
              ["Comparison", "speeds up decisions"],
              ["Myth-busting", "builds authority fast"],
              ["Testimonial / UGC", "strongest trust signal"],
              ["FAQ", "removes buying friction"],
            ].map(([k, v]) => (
              <li key={k} className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-white font-medium md:min-w-[170px]">{k}</span>
                <span className="text-[#A1A1AA] text-sm md:text-base">→ {v}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Each piece has a purpose — bullet list */}
        <div className="!mt-24 md:!mt-32">
          <h4 className="mk-card-title text-white mb-8">{T("s6_purpose_title")}</h4>
          <ul className="space-y-4 text-[#D4D4D8] list-disc list-outside pl-6 marker:text-[#C9A050]/70">
            <li>{T("s6_purpose_1")}</li>
            <li>{T("s6_purpose_2")}</li>
            <li>{T("s6_purpose_3")}</li>
          </ul>
        </div>

        <p className="!mt-20 md:!mt-28">{T("s6_close_1")}</p>
        <p className="mt-8">{T("s6_close_2")}</p>

        <p className="!mt-12 md:!mt-16 text-[#E4E4E7]">
          <span className="text-[#C9A050] mr-2">→</span>{T("s6_arrow")}
        </p>

        {/* Content structure — generous spacing */}
        <h4 className="mk-card-title text-white !mt-24 md:!mt-32 mb-8">{T("s6_structure_title")}</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7">
          {[
            [T("s6_struct_hook_t"), T("s6_struct_hook_b")],
            [T("s6_struct_ret_t"), T("s6_struct_ret_b")],
            [T("s6_struct_value_t"), T("s6_struct_value_b")],
          ].map(([k, v], i) => (
            <div key={i} className="border border-white/10 p-7 bg-white/[0.02]">
              <p className="overline text-[#C9A050]/70 mb-3">0{i + 1}</p>
              <p className="font-display text-2xl text-white">{k}</p>
              <p className="text-[#A1A1AA] text-sm mt-2">{v}</p>
            </div>
          ))}
        </div>

        <Final fieldId="s6_final">{T("s6_final")}</Final>
      </Section>

      <Divider />

      {/* 07 DISTRIBUTION */}
      <Section testid="mk-section-7" anchorId="mk-anchor-distribution" number={T("s7_overline")} title={<>{T("s7_title")}<span className="block text-[#8A8A93] mk-section-title" style={{ fontSize: "0.45em", marginTop: "0.5rem" }}>{T("s7_subtitle")}</span></>}>
        <p>{T("s7_body_1")}</p>
        <p>{T("s7_body_2")}</p>
        <p>{T("s7_body_3")}</p>
        <p>{T("s7_body_4")}</p>
        <p>{T("s7_body_5")}</p>

        <div className="!mt-16 md:!mt-24 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
          <div className="mk-card-gold p-7 md:p-9">
            <p className="overline text-[#C9A050]/80 mb-6">{T("s7_where_label")}</p>
            <ul className="space-y-3 text-[#E4E4E7]">
              <li>{T("s7_where_1")}</li><li>{T("s7_where_2")}</li><li>{T("s7_where_3")}</li><li>{T("s7_where_4")}</li>
            </ul>
          </div>
          <div className="mk-card-gold p-7 md:p-9">
            <p className="overline text-[#C9A050]/80 mb-6">{T("s7_we_label")}</p>
            <ul className="space-y-3 text-[#E4E4E7]">
              <li>{T("s7_we_1")}</li><li>{T("s7_we_2")}</li><li>{T("s7_we_3")}</li><li>{T("s7_we_4")}</li>
            </ul>
          </div>
        </div>

        <h4 className="mk-card-title text-white !mt-24 md:!mt-32">{T("s7_platforms_title")}</h4>

        <DistributionDiagram onPlatformClick={(slug) => { window.location.href = `/blog/${slug}`; }} />

        <div className="space-y-16 mt-12">
          <div>
            <p className="font-display text-xl md:text-2xl text-white mb-5">{T("s7_ig_title")}</p>
            <p>{T("s7_ig_lead")}</p>
            <ul className="mt-4 space-y-2 text-[#A1A1AA]">
              <li>• {T("s7_ig_b1")}</li><li>• {T("s7_ig_b2")}</li><li>• {T("s7_ig_b3")}</li>
            </ul>
            <p className="mt-6">{T("s7_ig_close")}</p>
            <p className="mt-5 text-[#E4E4E7]"><span className="text-[#C9A050] mr-2">→</span>{T("s7_ig_arrow")}</p>
          </div>
          <div>
            <p className="font-display text-xl md:text-2xl text-white mb-5">{T("s7_yt_title")}</p>
            <p>{T("s7_yt_lead")}</p>
            <ul className="mt-4 space-y-2 text-[#A1A1AA]">
              <li>• {T("s7_yt_b1")}</li><li>• {T("s7_yt_b2")}</li>
            </ul>
            <p className="mt-6">{T("s7_yt_close")}</p>
          </div>
        </div>

        <p className="mt-14">{T("s7_summary_1")}</p>
        <p>{T("s7_summary_2")}</p>

        <p className="mt-12 mk-quote text-[#E4E4E7] whitespace-pre-line">{T("s7_quote")}</p>

        <p className="mt-12 text-[#E4E4E7]">
          <span className="text-[#C9A050] mr-2">→</span>{T("s7_arrow")}
        </p>

        <Final fieldId="s7_final">{T("s7_final")}</Final>
      </Section>

      <Divider />

      {/* 08 FUNNEL */}
      <Section testid="mk-section-8" anchorId="mk-anchor-funnel" number={T("s8_overline")} title={T("s8_title")} panel>
        <p>{T("s8_body_1")}</p>
        <p className="mk-quote text-[#E4E4E7]">{T("s8_quote_1")}</p>
        <p>{T("s8_body_2")}</p>
        <p>{T("s8_body_3")}</p>

        <FunnelDiagram />

        <p className="overline text-[#C9A050]/70 mt-2"><G>{T("s8_for_label")}</G></p>

        <div className="mt-2 space-y-12">
          <FunnelStep n="01" title={T("s8_step1_t")} body={T("s8_step1_b")} />
          <FunnelStep n="02" title={T("s8_step2_t")} body={<span className="whitespace-pre-line">{T("s8_step2_b")}</span>} />
          <FunnelStep n="03" title={T("s8_step3_t")} body={<span className="whitespace-pre-line">{T("s8_step3_b")}</span>} />
          <FunnelStep n="04" title={T("s8_step4_t")} body={T("s8_step4_b")} />
          <FunnelStep n="05" title={T("s8_step5_t")} body={T("s8_step5_b")} gold />
        </div>

        <p className="mt-16">{T("s8_journey_eq")}</p>

        <p>{T("s8_conv_1")}</p>
        <p className="mk-quote text-[#E4E4E7]">{T("s8_conv_quote")}</p>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-white/10 p-7 bg-white/[0.02]">
            <p className="overline text-[#8A8A93] mb-3">{T("s8_without_t")}</p>
            <p>{T("s8_without_b")}</p>
          </div>
          <div className="border border-[#C9A050]/30 p-7 bg-[#C9A050]/[0.04]">
            <p className="overline text-[#C9A050]/70 mb-3">{T("s8_with_t")}</p>
            <p>{T("s8_with_b")}</p>
          </div>
        </div>

        <ul className="mt-16 space-y-4 text-[#E4E4E7]">
          <li><span className="text-[#C9A050] mr-2">→</span>{T("s8_arrow_1")}</li>
          <li><span className="text-[#C9A050] mr-2">→</span>{T("s8_arrow_2")}</li>
          <li><span className="text-[#C9A050] mr-2">→</span>{T("s8_arrow_3")}</li>
        </ul>

        <Final fieldId="s8_final">{T("s8_final")}</Final>
      </Section>

      <Divider />

      {/* CUSTOMER PSYCHOLOGY */}
      <Section testid="mk-section-psychology" number={T("psy_overline")} title={T("psy_title")}>
        <h3 className="font-display text-3xl md:text-4xl text-white">{T("psy_section_h")}</h3>
        <p className="mk-quote text-[#E4E4E7]">{T("psy_quote")}</p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
          <div className="mk-card-gold p-7 md:p-9">
            <p className="overline text-[#C9A050]/80 mb-7">{T("psy_drives_label")}</p>
            <ul className="space-y-4 text-[#E4E4E7]">
              {[1, 2, 3, 4].map((n) => (
                <li key={n}>
                  <span className="text-white font-medium">{T(`psy_drive_${n}_k`)}</span>
                  <span className="text-[#A1A1AA]"> {T(`psy_drive_${n}_v`)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mk-card-gold p-7 md:p-9">
            <p className="overline text-[#C9A050]/80 mb-7">{T("psy_trust_label")}</p>
            <ul className="space-y-4 text-[#E4E4E7]">
              {[1, 2, 3].map((n) => (
                <li key={n}>
                  <span className="text-white font-medium">{T(`psy_trust_${n}_k`)}</span>
                  <span className="text-[#A1A1AA]"> {T(`psy_trust_${n}_v`)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="mt-16 text-[#E4E4E7]"><span className="text-[#C9A050] mr-2">→</span>{T("psy_arrow")}</p>
      </Section>

      <Divider />

      {/* OFFER */}
      <Section testid="mk-section-offer" number={T("off_overline")} title={T("off_title")}>
        <p>{T("off_body_1")}</p>
        <p>{T("off_body_2")}</p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            [T("off_value_t"), T("off_value_b")],
            [T("off_clarity_t"), T("off_clarity_b")],
            [T("off_risk_t"), T("off_risk_b")],
            [T("off_urg_t"), T("off_urg_b")],
          ].map(([k, v], i) => (
            <div key={i} className="border border-white/10 p-7 bg-white/[0.02]">
              <p className="font-display text-xl md:text-2xl text-white">{k}</p>
              <p className="text-[#A1A1AA] text-sm mt-2">{v}</p>
            </div>
          ))}
        </div>
        <ul className="mt-16 space-y-4 text-[#E4E4E7]">
          <li><span className="text-[#C9A050] mr-2">→</span>{T("off_arrow_1")}</li>
          <li><span className="text-[#C9A050] mr-2">→</span>{T("off_arrow_2")}</li>
        </ul>
      </Section>

      <Divider />

      {/* FULL SYSTEM */}
      <Section testid="mk-section-system" title={T("sys_title")}>
        <p>{T("sys_body_1")}</p>
        <p className="mk-quote text-[#E4E4E7]">{T("sys_quote")}</p>

        <SystemDiagram onNodeClick={scrollToAnchor} />

        <div className="my-16 md:my-24 text-center">
          <p className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight text-white">
            {T("sys_equation_1")} <span className="text-[#C9A050]">=</span> {T("sys_equation_2")}{" "}
            <span className="text-[#C9A050]">×</span> {T("sys_equation_3")}{" "}
            <span className="text-[#C9A050]">×</span> <G>{T("sys_equation_4")}</G>
          </p>
        </div>

        <p>{T("sys_close_1")}</p>
        <p className="mt-12 text-[#E4E4E7]"><span className="text-[#C9A050] mr-2">→</span>{T("sys_arrow")}</p>
      </Section>

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-20 py-28 md:py-40 text-center">
        <p className="overline text-[#C9A050]/70 mb-10">{T("cta_overline")}</p>
        <h3 className="font-display text-3xl md:text-5xl tracking-tight text-white max-w-3xl mx-auto leading-tight">{T("cta_headline")}</h3>
        <Link to="/contact" data-testid="mk-cta" className="inline-block mt-14 overline border border-[#C9A050] text-[#C9A050] hover:bg-[#C9A050] hover:text-black px-7 py-4 transition-all">
          {T("cta_button")}
        </Link>
      </section>

      {/* DISCLAIMER */}
      <div data-testid="mk-disclaimer" className="px-6 md:px-12 lg:px-20 pb-20 md:pb-24">
        <div className="max-w-4xl mx-auto border-t border-[#C9A050]/15 pt-12">
          <p className="text-center text-sm md:text-base text-[#A1A1AA] tracking-normal leading-relaxed font-light">
            {T("disclaimer")}
          </p>
        </div>
      </div>
    </main>
  );
}

/* -------- Highlight rules: gold-words + underline-words ----------
   Wraps case-insensitive whole-word matches inside body copy with a
   styled <span>. Strict scoping:
    - Only descends into elements with class .mk-body, .mk-final, .mk-quote
      or <li> nodes inside .mk-card-gold; explicitly skips headings
      (.mk-section-title, .mk-section-label, .mk-hero-title, .mk-pillar-title,
      .mk-card-title, h1-h4) so e.g. "Target Audience" headings stay clean.
    - Never re-wraps already-wrapped content (idempotent).
    - Runs only on the /marketing page (this hook lives in Marketing.jsx).
*/
function parseHighlightList(raw) {
  if (!raw || typeof raw !== "string") return [];
  return raw
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((s, i, a) => a.indexOf(s) === i); // dedupe
}

const HIGHLIGHT_SKIP = "h1, h2, h3, h4, .mk-section-title, .mk-section-label, .mk-hero-title, .mk-pillar-title, .mk-card-title";
const HIGHLIGHT_INCLUDE = ".mk-body, .mk-final, .mk-quote, .mk-card-gold";

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function buildPattern(words) {
  if (!words.length) return null;
  // Sort longer first so multi-word phrases match before single tokens.
  const ordered = [...words].sort((a, b) => b.length - a.length);
  const escaped = ordered.map(escapeRegex).join("|");
  // \b works for ASCII; for unicode we use lookarounds against word chars.
  return new RegExp(`(?<![A-Za-z0-9_])(${escaped})(?![A-Za-z0-9_])`, "gi");
}

function applyHighlightsToTextNode(node, pattern, className, marker) {
  if (!node || !node.parentNode) return false;
  const text = node.nodeValue;
  if (!pattern || !text || !pattern.test(text)) return false;
  pattern.lastIndex = 0;
  const frag = document.createDocumentFragment();
  let last = 0;
  let m;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) {
      frag.appendChild(document.createTextNode(text.slice(last, m.index)));
    }
    const span = document.createElement("span");
    span.className = className;
    span.dataset.mkHighlight = marker;
    span.textContent = m[0];
    frag.appendChild(span);
    last = m.index + m[0].length;
  }
  if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
  if (!node.parentNode) return false;
  try {
    node.parentNode.replaceChild(frag, node);
  } catch (_e) {
    return false;
  }
  return true;
}

function walkAndHighlight(root, pattern, className, marker) {
  if (!root || !pattern) return;
  // Collect text nodes that aren't inside skip selectors and ARE inside an
  // include selector ancestor.
  const skipNodes = new Set();
  root.querySelectorAll(HIGHLIGHT_SKIP).forEach((el) => skipNodes.add(el));

  const includeRoots = root.querySelectorAll(HIGHLIGHT_INCLUDE);
  const candidateTexts = [];
  includeRoots.forEach((includeRoot) => {
    const walker = document.createTreeWalker(includeRoot, NodeFilter.SHOW_TEXT, {
      acceptNode(n) {
        if (!n.nodeValue || !n.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
        // Skip if any ancestor is in skipNodes.
        let p = n.parentNode;
        while (p && p !== includeRoot) {
          if (skipNodes.has(p)) return NodeFilter.FILTER_REJECT;
          // Avoid re-wrapping already-highlighted spans.
          if (p.nodeType === 1 && p.dataset && p.dataset.mkHighlight) return NodeFilter.FILTER_REJECT;
          p = p.parentNode;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    let n;
    while ((n = walker.nextNode())) candidateTexts.push(n);
  });
  candidateTexts.forEach((n) => applyHighlightsToTextNode(n, pattern, className, marker));
}

function unwrapHighlights(root, marker) {
  if (!root) return;
  const matches = root.querySelectorAll(`span[data-mk-highlight="${marker}"]`);
  matches.forEach((span) => {
    const parent = span.parentNode;
    if (!parent) return;
    try {
      parent.replaceChild(document.createTextNode(span.textContent), span);
      parent.normalize();
    } catch (_e) {
      /* DOM moved underneath us — let the next pass clean up. */
    }
  });
}

function useHighlights(goldList, underlineList, deps) {
  useEffect(() => {
    const root = document.querySelector(".mk-page");
    if (!root) return undefined;
    // Defer so React commits + framer-motion animations have settled into the DOM.
    const t = setTimeout(() => {
      // Unwrap any previous highlights from prior renders, then re-apply.
      unwrapHighlights(root, "gold");
      unwrapHighlights(root, "underline");
      const goldPattern = buildPattern(goldList);
      const underlinePattern = buildPattern(underlineList);
      // Apply underline first so gold can layer on top if both lists overlap.
      if (underlinePattern) walkAndHighlight(root, underlinePattern, "mk-underline", "underline");
      if (goldPattern) walkAndHighlight(root, goldPattern, "mk-gold", "gold");
    }, 80);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goldList.join("|"), underlineList.join("|"), ...(deps || [])]);
}

function MiniBox({ label, body }) {
  return (
    <div className="mk-card-gold p-7">
      <p className="overline text-[#C9A050]/80 mb-3">{label}</p>
      <p className="text-[#E4E4E7] text-sm md:text-base">{body}</p>
    </div>
  );
}

function FunnelStep({ n, title, body, gold }) {
  return (
    <div className="grid grid-cols-[auto_1fr] gap-5 md:gap-7 items-start">
      <span className={`overline mt-1 ${gold ? "text-[#C9A050]" : "text-[#8A8A93]"}`}>{n}</span>
      <div>
        <p className={`font-display text-xl md:text-2xl mb-2 ${gold ? "text-[#C9A050]" : "text-white"}`}>{title}</p>
        <p className="text-[#A1A1AA]">{body}</p>
      </div>
    </div>
  );
}

function BrandRow({ slug, name, body, tail, logo_url, logo_visible, monochrome }) {
  const showLogo = logo_visible !== false && !!logo_url;
  return (
    <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6 md:gap-12 items-start border-t border-white/5 pt-10 md:pt-14">
      <Brand slug={slug}>
        <span className="flex items-center gap-4 flex-wrap">
          {showLogo && (
            <img src={logo_url} alt={`${name} logo`} className={`h-7 md:h-8 w-auto object-contain ${monochrome ? "mk-logo-mono" : ""}`} />
          )}
          <span className="font-display text-2xl md:text-3xl">{name}</span>
        </span>
      </Brand>
      <div className="text-[#A1A1AA] leading-relaxed">
        {body}
        {tail && (<><br /><span className="text-[#D4D4D8]">{tail}</span></>)}
      </div>
    </div>
  );
}
